import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // If returning from magic link login, clean up the URL
    if (window.location.search?.includes('magic_login=success')) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    try {
      // Check via user-auth (JWT) first
      const response = await axios.get(`${API}/user-auth/check`, {
        withCredentials: true
      });

      if (response.data.authenticated) {
        setUser(response.data.user);
        setLoading(false);
        return;
      }
    } catch {
      // JWT check failed, try session-based auth
    }

    try {
      // Fallback: check via Emergent Auth session
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });

      if (response.data?.email) {
        setUser(response.data);
        setLoading(false);
        return;
      }
    } catch {
      // Session check also failed
    }

    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      // Try both logout endpoints
      await Promise.allSettled([
        axios.post(`${API}/user-auth/logout`, {}, { withCredentials: true }),
        axios.post(`${API}/auth/logout`, {}, { withCredentials: true }),
      ]);
    } catch {
      // Ignore errors
    }
    setUser(null);
    window.location.href = '/login';
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    setUser,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isToolManager: user?.role === 'tool_manager'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
